import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useMeetings } from "@/providers/MeetingsProvider";
import { useToast } from "@/hooks/use-toast";
import type { MeetingDto } from "@shared/types/MeetingDto";

const CANCEL_REASONS = [
  { value: "scheduling_conflict", label: "Scheduling conflict" },
  { value: "no_longer_needed", label: "No longer needed" },
  { value: "client_request", label: "Client request" },
  { value: "other", label: "Other" },
];

interface CancelMeetingDialogProps {
  meeting: MeetingDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CancelMeetingDialog({
  meeting,
  open,
  onOpenChange,
}: CancelMeetingDialogProps) {
  const { performAction, isActionLoading } = useMeetings();
  const { toast } = useToast();
  const [reason, setReason] = useState("");
  const [otherText, setOtherText] = useState("");
  const [notifyParticipants, setNotifyParticipants] = useState(true);
  const [notifyMessage, setNotifyMessage] = useState(
    "This meeting has been cancelled. We apologize for any inconvenience."
  );

  const isValid = reason !== "" && (reason !== "other" || otherText.trim().length > 0);

  const handleConfirm = async () => {
    if (!meeting || !isValid) return;

    const cancelReason = reason === "other" ? otherText.trim() : reason;

    try {
      await performAction(meeting.id, {
        action: "CANCEL",
        cancelReason,
      });

      toast({
        title: "Meeting cancelled",
        description: notifyParticipants
          ? "Participants have been notified."
          : "Meeting cancelled successfully.",
        variant: "success",
      });

      resetAndClose();
    } catch (err) {
      toast({
        title: "Failed to cancel meeting",
        description:
          err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    }
  };

  const resetAndClose = () => {
    setReason("");
    setOtherText("");
    setNotifyParticipants(true);
    setNotifyMessage(
      "This meeting has been cancelled. We apologize for any inconvenience."
    );
    onOpenChange(false);
  };

  if (!meeting) return null;

  const scheduledDate = meeting.scheduledAt
    ? new Date(meeting.scheduledAt).toLocaleString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "Not yet scheduled";

  return (
    <Dialog open={open} onOpenChange={resetAndClose}>
      <DialogContent
        className="sm:max-w-md"
        aria-label="Cancel meeting dialog"
      >
        <DialogHeader>
          <DialogTitle>Cancel meeting?</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="bg-muted/50 rounded-lg p-3 space-y-1 text-sm">
            <p className="font-medium">{meeting.title}</p>
            <p className="text-muted-foreground">{scheduledDate}</p>
            {meeting.timezone && (
              <p className="text-muted-foreground text-xs">
                {meeting.timezone}
              </p>
            )}
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium">
              Reason for cancellation *
            </Label>
            <RadioGroup value={reason} onValueChange={setReason}>
              {CANCEL_REASONS.map((r) => (
                <div key={r.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={r.value} id={`reason-${r.value}`} />
                  <Label
                    htmlFor={`reason-${r.value}`}
                    className="font-normal cursor-pointer"
                  >
                    {r.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>

            {reason === "other" && (
              <Textarea
                placeholder="Please specify the reason..."
                value={otherText}
                onChange={(e) => setOtherText(e.target.value.slice(0, 500))}
                className="mt-2"
                maxLength={500}
              />
            )}
          </div>

          <div className="space-y-3 pt-2 border-t border-border/50">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="notify"
                checked={notifyParticipants}
                onCheckedChange={(checked) =>
                  setNotifyParticipants(checked === true)
                }
              />
              <Label htmlFor="notify" className="font-normal cursor-pointer">
                Notify participants
              </Label>
            </div>

            {notifyParticipants && (
              <Input
                placeholder="Optional message to participants"
                value={notifyMessage}
                onChange={(e) => setNotifyMessage(e.target.value)}
              />
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={resetAndClose}>
            Keep meeting
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!isValid || isActionLoading}
            aria-label={`Confirm cancel meeting: ${meeting.title}`}
          >
            {isActionLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Cancelling...
              </>
            ) : (
              "Confirm cancel"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
